import { createFormatForDescription as cffd } from '../createFormatForDescription'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import { MatchFlag, Flag, matchFlags } from '../flag'
import AccountSetInstructions from '../types/instructions/AccountSetInstructions'
import TransactionType from '../types/TransactionType'
import { hexToAscii } from '../util'

/**
 * An annotation for AccountSet transactions.
 *
 * @property type - The transaction type.
 * @property additionalFlags - Payment type specific flags.
 * @property clearFlag - An explanation of the clearFlag.
 * @property domain - An explanation of the domain.
 * @property emailHash - An explanation of the emailHash.
 * @property messageKey - An explanation of the messageKey.
 * @property setFlag - An explanation of the setFlag.
 * @property transferRate - An explanation of the transferRate.
 * @property tickSize - An explanation of the tickSize.
 */
export interface AccountSetAnnotation {
  readonly type: TransactionType.AccountSet
  readonly additionalFlags: Flag[]

  readonly clearFlag?: Explanation<Flag>
  readonly domain?: Explanation<string>
  readonly emailHash?: Explanation<string>
  readonly messageKey?: Explanation<string>
  readonly setFlag?: Explanation<Flag>
  readonly transferRate?: Explanation<number>
  readonly tickSize?: Explanation<number>
}

/**
 * Represents the names of the possible flags that can appear in AccountSet transactions.
 *
 * @enum {number}
 */
enum FlagName {
  AccountTxnID = 'asfAccountTxnID',
  DefaultRipple = 'asfDefaultRipple',
  DepositAuth = 'asfDepositAuth',
  DisableMaster = 'asfDisableMaster',
  DisallowXRP = 'asfDisallowXRP',
  GlobalFreeze = 'asfGlobalFreeze',
  NoFreeze = 'asfNoFreeze',
  RequireAuth = 'asfRequireAuth',
  RequireDest = 'asfRequireDest',
}

/**
 * Represents the codes of each flag that can appear in AccountSet transactions.
 *
 * @enum {number}
 */
enum FlagCode {
  AccountTxnID = 5,
  DefaultRipple = 8,
  DepositAuth = 9,
  DisableMaster = 4,
  DisallowXRP = 3,
  GlobalFreeze = 7,
  NoFreeze = 6,
  RequireAuth = 2,
  RequireDest = 1,
}

enum FlagDescription {
  AccountTxnID = "Track the ID of this account's most recent transaction. Required for AccountTxnID.",
  DefaultRipple = "Enable rippling on this account's trust lines by default.",
  DepositAuth = 'Enable Deposit Authorization on this account. (Added by the DepositAuth amendment.)',
  DisableMaster = 'Disallow use of the master key pair. Can only be enabled if the account has configured another way to sign transactions, such as a Regular Key or a Signer List.',
  DisallowXRP = 'XRP should not be sent to this account. (Enforced by client applications, not by rippled)',
  GlobalFreeze = 'Freeze all assets issued by this account.',
  NoFreeze = 'Permanently give up the ability to freeze individual trust lines or disable Global Freeze. This flag can never be disabled after being enabled.',
  RequireAuth = 'Require authorization for users to hold balances issued by this address. Can only be enabled if the address has no trust lines connected to it.',
  RequireDest = 'Require a destination tag to send transactions to this account.',
}

const numericalFlags: { [key in FlagCode]: Flag } = {
  [FlagCode.AccountTxnID]: {
    name: FlagName.AccountTxnID,
    label: FlagDescription.AccountTxnID,
  },
  [FlagCode.DefaultRipple]: {
    name: FlagName.DefaultRipple,
    label: FlagDescription.DefaultRipple,
  },
  [FlagCode.DepositAuth]: {
    name: FlagName.DepositAuth,
    label: FlagDescription.DepositAuth,
  },
  [FlagCode.DisableMaster]: {
    name: FlagName.DisableMaster,
    label: FlagDescription.DisableMaster,
  },
  [FlagCode.DisallowXRP]: {
    name: FlagName.DisallowXRP,
    label: FlagDescription.DisallowXRP,
  },
  [FlagCode.GlobalFreeze]: {
    name: FlagName.GlobalFreeze,
    label: FlagDescription.GlobalFreeze,
  },
  [FlagCode.NoFreeze]: {
    name: FlagName.NoFreeze,
    label: FlagDescription.NoFreeze,
  },
  [FlagCode.RequireAuth]: {
    name: FlagName.RequireAuth,
    label: FlagDescription.RequireAuth,
  },
  [FlagCode.RequireDest]: {
    name: FlagName.RequireDest,
    label: FlagDescription.RequireDest,
  },
}

/**
 * Match a code to a single numerical flag.
 *
 * @param code - The code to be matched.
 *
 * @returns The matched flag if found. Otherwise, returns a 'noFlagMatch' flag.
 */
function matchNumericalFlags(code: number): Flag {
  if (Object.values(FlagCode).includes(code)) {
    return numericalFlags[code as FlagCode]
  }

  return {
    name: 'noFlagMatch',
    label: 'No flags matched the given code',
  }
}

/**
 * Represents the possible bit/hex flag names that can appear in AccountSet transactions.
 *
 * @enum {number}
 */
enum HexFlagName {
  RequireDestTag = 'tfRequireDestTag',
  OptionalDestTag = 'tfOptionalDestTag',
  RequireAuth = 'tfRequireAuth',
  OptionalAuth = 'tfOptionalAuth',
  DisallowXRP = 'tfDisallowXRP',
  AllowXrp = 'tfAllowXrp',
}

/**
 * Represents the possible bit/hex flag codes that can appear in AccountSet transactions.
 *
 * @enum {number}
 */
enum HexFlagCode {
  RequireDestTag = 0x00010000,
  OptionalDestTag = 0x00020000,
  RequireAuth = 0x00040000,
  OptionalAuth = 0x00080000,
  DisallowXRP = 0x00100000,
  AllowXrp = 0x00200000,
}

const hexFlags: MatchFlag[] = [
  {
    code: HexFlagCode.RequireDestTag,
    name: HexFlagName.RequireDestTag,
    label: `Deprecated: Enable ${FlagName.RequireDest} flag (${FlagDescription.RequireDest})`,
  },
  {
    code: HexFlagCode.RequireDestTag,
    name: HexFlagName.OptionalDestTag,
    label: `Deprecated: Disable ${FlagName.RequireDest} flag (${FlagDescription.RequireDest})`,
  },
  {
    code: HexFlagCode.RequireDestTag,
    name: HexFlagName.RequireAuth,
    label: `Deprecated: Enable ${FlagName.RequireAuth} flag (${FlagDescription.RequireAuth})`,
  },
  {
    code: HexFlagCode.RequireDestTag,
    name: HexFlagName.OptionalAuth,
    label: `Deprecated: Disable ${FlagName.RequireAuth} flag (${FlagDescription.RequireAuth})`,
  },
  {
    code: HexFlagCode.RequireDestTag,
    name: HexFlagName.DisallowXRP,
    label: `Deprecated: Enable ${FlagName.DisallowXRP} flag (${FlagDescription.DisallowXRP})`,
  },
  {
    code: HexFlagCode.RequireDestTag,
    name: HexFlagName.AllowXrp,
    label: `Deprecated: Disable ${FlagName.DisallowXRP} flag (${FlagDescription.DisallowXRP})`,
  },
]

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: AccountSetInstructions,
): AccountSetAnnotation {
  const additionalFlags = matchFlags(transactionInstructions.Flags, hexFlags)

  const domain = explainOptional(
    transactionInstructions.Domain,
    (theDomain) =>
      `${cffd(hexToAscii(theDomain), '')} (domain) owns this account.`,
  )

  const emailHash = explainOptional(
    transactionInstructions.EmailHash,
    (email) =>
      `The hash of the email address associated with this account is ${cffd(
        email,
        'email',
      )}`,
  )

  const messageKey = explainOptional(
    transactionInstructions.MessageKey,
    (key) =>
      `Send encrypted messages to this account using public key ${cffd(
        key,
        'key',
      )}`,
  )

  const transferRate = explainOptional(
    transactionInstructions.TransferRate,
    (rate) =>
      `This account charges ${cffd(
        rate.toString(),
        'transferRate',
      )} (in billionths of a unit) to other users when they transfer this account's issued currency.`,
  )

  const tickSize = explainOptional(
    transactionInstructions.TickSize,
    (size) =>
      `The tick size to use for offers involving a currency issued by this address is ${cffd(
        size.toString(),
        'tickSize',
      )}`,
  )

  const clearFlag = transactionInstructions.ClearFlag
    ? explain(
        matchNumericalFlags(transactionInstructions.ClearFlag),
        'This flag will be removed from the account',
      )
    : undefined

  const setFlag = transactionInstructions.SetFlag
    ? explain(
        matchNumericalFlags(transactionInstructions.SetFlag),
        'This flag will be added to the account',
      )
    : undefined

  return {
    additionalFlags,
    ...(domain && { domain }),
    ...(emailHash && { emailHash }),
    ...(messageKey && { messageKey }),
    ...(transferRate && { transferRate }),
    ...(tickSize && { tickSize }),
    ...(clearFlag && { clearFlag }),
    ...(setFlag && { setFlag }),

    type: TransactionType.AccountSet,
  }
}
